import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, Subject, forkJoin, of, takeUntil } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import {
  EntitySelectComponent,
  EntitySelectPage
} from '../../../../../shared/components/entity-select/entity-select.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { UserApiService } from '../../../../users/services/user-api.service';
import { CrmUserResponse } from '../../../../users/models/user.model';
import { ESCALATION_ACTION_LABELS, ESCALATION_TRIGGER_LABELS } from '../../../utils/ticket-format.util';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import {
  TicketEscalationActionType,
  TicketEscalationRule,
  TicketEscalationTriggerType,
  TicketPipeline,
  TicketPriority
} from '../../../models/tickets-config.model';

/** Shape del JSON `triggerConfig` que evalúa el backend (TicketEscalationApplyListener). */
interface EscalationTriggerConfig {
  breachKind?: 'RESPONSE' | 'RESOLUTION';
  reopenCountGte?: number;
}

/** Shape del JSON `actionConfig`: steps / toUserId / newPipelineId según la acción. */
interface EscalationActionConfig {
  steps?: number;
  toUserId?: number;
  newPipelineId?: number;
}

@Component({
  selector: 'app-tickets-escalation-rules-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ButtonComponent, EntitySelectComponent],
  templateUrl: './escalation-rules-form.component.html'
})
export class TicketsEscalationRulesFormComponent implements OnInit, OnDestroy {
  @Output() saved = new EventEmitter<TicketEscalationRule>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private toast = inject(ToastService);
  private userApi = inject(UserApiService);
  private auth = inject(AuthService);

  isEditing = false;
  itemId: number | null = null;
  saving = false;
  error: string | null = null;
  private originalName = '';

  readonly triggerTypes: TicketEscalationTriggerType[] = [
    'SLA_BREACH',
    'SLA_WARNING',
    'REOPEN_THRESHOLD',
    'MANUAL'
  ];

  readonly triggerLabels = ESCALATION_TRIGGER_LABELS as Record<TicketEscalationTriggerType, string>;

  readonly actionTypes: TicketEscalationActionType[] = [
    'INCREASE_PRIORITY',
    'REASSIGN_NEXT_LEVEL',
    'NOTIFY_MANAGER',
    'CHANGE_PIPELINE'
  ];

  readonly actionLabels = ESCALATION_ACTION_LABELS as Record<TicketEscalationActionType, string>;

  readonly breachKinds: { value: 'RESPONSE' | 'RESOLUTION'; label: string }[] = [
    { value: 'RESPONSE', label: 'Primera respuesta' },
    { value: 'RESOLUTION', label: 'Resolución' }
  ];

  // Catálogo para CHANGE_PIPELINE.
  pipelines: TicketPipeline[] = [];

  // INC 65: catálogo de prioridades para el dropdown de orden de evaluación.
  priorities: TicketPriority[] = [];

  // Configuración del trigger.
  breachKind: '' | 'RESPONSE' | 'RESOLUTION' = '';
  reopenCountGte: number | null = null;

  // Configuración de la acción.
  steps: number | null = 1;
  toUserId: number | null = null;
  toUserDisplay = '';
  newPipelineId: number | null = null;

  private usersCache: CrmUserResponse[] | null = null;

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
    description: [''],
    priority: [10],
    triggerType: ['SLA_BREACH' as TicketEscalationTriggerType, [Validators.required]],
    actionType: ['INCREASE_PRIORITY' as TicketEscalationActionType, [Validators.required]],
    isActive: [true]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    forkJoin({
      pipelines: this.configApi.getPipelines().pipe(catchError(() => of([] as TicketPipeline[]))),
      priorities: this.configApi.getPriorities().pipe(catchError(() => of([] as TicketPriority[])))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ pipelines, priorities }) => {
        this.pipelines = pipelines;
        this.priorities = priorities;
        // INC 65: reconcilia el orden numérico existente con el `level` más cercano.
        this.normalizePriorityControl();
      });
  }

  /** Etiqueta legible de una prioridad para el dropdown de orden (INC 65). */
  priorityOptionLabel(p: TicketPriority): string {
    return (p.label && p.label.trim()) || p.name;
  }

  /**
   * INC 65: el control `priority` guarda el orden de evaluación como el `level`
   * de una prioridad. Si el valor actual no coincide con ningún `level`
   * disponible, lo ajusta al más cercano para preseleccionar el dropdown.
   */
  private normalizePriorityControl(): void {
    if (!this.priorities.length) return;
    const ctrl = this.form.get('priority');
    const current = Number(ctrl?.value ?? 10);
    const levels = this.priorities.map((p) => p.level);
    if (levels.includes(current)) return;
    const closest = levels.reduce((a, b) =>
      Math.abs(b - current) < Math.abs(a - current) ? b : a
    );
    ctrl?.setValue(closest, { emitEvent: false });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.auth.getCurrentUser()?.businessId || 1;
  }

  // ===== EntitySelect: usuarios (arrow fns para conservar `this`) =====
  userSearchFn = (term: string): Observable<EntitySelectPage<CrmUserResponse>> => {
    const build = (list: CrmUserResponse[]): EntitySelectPage<CrmUserResponse> => {
      const t = term.trim().toLowerCase();
      const items = t
        ? list.filter(
            (u) =>
              `${u.fullname} ${u.lastname}`.toLowerCase().includes(t) ||
              (u.mail || '').toLowerCase().includes(t)
          )
        : list;
      return { items, totalPages: 1 };
    };
    if (this.usersCache) {
      return of(build(this.usersCache));
    }
    return this.userApi.listActiveByBusiness(this.businessId).pipe(
      map((list) => {
        this.usersCache = list;
        return build(list);
      })
    );
  };

  userLabelFn = (u: CrmUserResponse): string => `${u.fullname} ${u.lastname}`.trim();
  userSublabelFn = (u: CrmUserResponse): string => u.mail || '';
  userIdFn = (u: CrmUserResponse): number => u.id;

  // ===== Visibilidad de los controles según trigger/acción =====
  get triggerType(): TicketEscalationTriggerType {
    return this.form.get('triggerType')!.value as TicketEscalationTriggerType;
  }
  get actionType(): TicketEscalationActionType {
    return this.form.get('actionType')!.value as TicketEscalationActionType;
  }

  get triggerUsesBreachKind(): boolean {
    return this.triggerType === 'SLA_BREACH' || this.triggerType === 'SLA_WARNING';
  }
  get triggerUsesReopen(): boolean {
    return this.triggerType === 'REOPEN_THRESHOLD';
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = null;
    this.saving = false;
    this.breachKind = '';
    this.reopenCountGte = null;
    this.steps = 1;
    this.toUserId = null;
    this.toUserDisplay = '';
    this.newPipelineId = null;
    this.form.reset({
      name: '',
      description: '',
      priority: 10,
      triggerType: 'SLA_BREACH',
      actionType: 'INCREASE_PRIORITY',
      isActive: true
    });
  }

  loadRule(id: number): void {
    this.isEditing = true;
    this.itemId = id;
    this.configApi.getEscalationRuleById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          description: item.description ?? '',
          priority: item.priority ?? 10,
          triggerType: item.triggerType,
          actionType: item.actionType,
          isActive: item.isActive
        });
        this.originalName = item.name;

        const tc = (item.triggerConfig ?? {}) as EscalationTriggerConfig;
        this.breachKind = tc.breachKind ?? '';
        this.reopenCountGte = tc.reopenCountGte ?? null;

        const ac = (item.actionConfig ?? {}) as EscalationActionConfig;
        this.steps = ac.steps ?? 1;
        this.toUserId = ac.toUserId ?? null;
        this.newPipelineId = ac.newPipelineId ?? null;
        this.resolveUserDisplay(this.toUserId);
        // INC 65: preseleccionar la opción del dropdown de prioridad.
        this.normalizePriorityControl();
      },
      error: (err) => { this.error = err.error?.message || 'Error al cargar regla'; }
    });
  }

  private resolveUserDisplay(userId: number | null): void {
    if (userId == null) { this.toUserDisplay = ''; return; }
    this.userApi.listActiveByBusiness(this.businessId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (list) => {
        this.usersCache = list;
        const u = list.find((x) => x.id === userId);
        this.toUserDisplay = u ? this.userLabelFn(u) : `Usuario #${userId}`;
      },
      error: () => { this.toUserDisplay = `Usuario #${userId}`; }
    });
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }

    // Construir triggerConfig según el tipo de trigger.
    let triggerConfig: EscalationTriggerConfig | undefined;
    if (this.triggerUsesBreachKind) {
      triggerConfig = this.breachKind ? { breachKind: this.breachKind } : undefined;
    } else if (this.triggerUsesReopen) {
      if (this.reopenCountGte == null || this.reopenCountGte < 1) {
        this.error = 'Indica el número mínimo de reaperturas (al menos 1).';
        return;
      }
      triggerConfig = { reopenCountGte: Number(this.reopenCountGte) };
    }

    // Construir actionConfig según la acción.
    let actionConfig: EscalationActionConfig | undefined;
    switch (this.actionType) {
      case 'INCREASE_PRIORITY':
        actionConfig = { steps: this.steps != null && this.steps >= 1 ? Number(this.steps) : 1 };
        break;
      case 'REASSIGN_NEXT_LEVEL':
        if (this.toUserId == null) {
          this.error = 'Selecciona el usuario al que se reasignará el ticket.';
          return;
        }
        actionConfig = { toUserId: this.toUserId };
        break;
      case 'CHANGE_PIPELINE':
        if (this.newPipelineId == null) {
          this.error = 'Selecciona el pipeline destino.';
          return;
        }
        actionConfig = { newPipelineId: this.newPipelineId };
        break;
      case 'NOTIFY_MANAGER':
        actionConfig = undefined;
        break;
    }

    this.saving = true;
    this.error = null;
    const raw = this.form.value;

    const req = {
      name: raw.name,
      description: raw.description || undefined,
      priority: Number(raw.priority ?? 10),
      triggerType: raw.triggerType as TicketEscalationTriggerType,
      triggerConfig,
      actionType: raw.actionType as TicketEscalationActionType,
      actionConfig,
      isActive: !!raw.isActive
    };

    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updateEscalationRule(this.itemId, req)
      : this.configApi.createEscalationRule(req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Regla de escalación',
          this.originalName !== (raw.name?.trim() || '')
            ? `Se actualizó "${this.originalName}" → "${raw.name?.trim() || ''}".`
            : `Se actualizó "${raw.name?.trim() || ''}" correctamente.`);
        else this.toast.created('Regla de escalación', `Se creó "${raw.name?.trim() || ''}" correctamente.`);
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar la regla';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void { this.cancelled.emit(); }
}
